import React, { useEffect } from 'react';
import {
  AppBar,
  Box,
  CssBaseline,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  useTheme,
  alpha
} from '@mui/material';
import {
  Menu as MenuIcon,
  CalendarMonth as CalendarIcon,
  People as PeopleIcon,
  Dashboard as DashboardIcon,
  Logout as LogoutIcon,
  Groups as GroupsIcon,
  Settings as SettingsIcon,
  Person as PersonIcon,
  Security as SecurityIcon,
  CreditCard as CreditCardIcon,
  CardGiftcard as ReferralIcon,
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon,
  EventNote as SessionsIcon,
  Notifications as NotificationsIcon,
  Palette as BrandingIcon,
  AccessTime as WaitingListIcon,
  Description as DocumentsIcon,
  EventAvailable as OnlineBookingIcon,
  LibraryBooks as TemplatesIcon,
  Assessment as ReportsIcon,
  HealthAndSafety as InsuranceIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { useNavigate, Outlet } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useThemeMode } from '../../contexts/ThemeContext';
import { useOrganization } from '../../contexts/OrganizationContext';
import FeatureLockedItem from '../common/FeatureLockedItem';
import AppBreadcrumbs from '../common/AppBreadcrumbs';
import HelpDrawerContent, { HelpFAB } from '../help/HelpDrawer';
import { PlanId } from '../../config/plans';

const drawerWidth = 240;
const SYSTEM_ADMIN_EMAIL = 'admin@clinafy.com';

// Helper to check if plan allows feature
const PLAN_HIERARCHY: PlanId[] = ['trial', 'individual', 'small_clinic', 'enterprise'];

const isPlanAtLeast = (currentPlan: PlanId, requiredPlan: PlanId): boolean => {
  const currentIndex = PLAN_HIERARCHY.indexOf(currentPlan);
  const requiredIndex = PLAN_HIERARCHY.indexOf(requiredPlan);
  return currentIndex >= requiredIndex;
};

const Layout: React.FC = () => {
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [helpOpen, setHelpOpen] = React.useState(false);
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const { isDarkMode, toggleDarkMode } = useThemeMode();
  const { organization } = useOrganization();

  // Get current plan and determine locked features
  const currentPlan = (organization?.planId as PlanId) || 'trial';

  // Feature lock checks - based on plan only (permission checks done in pages)
  const isTeamLocked = !isPlanAtLeast(currentPlan, 'small_clinic');
  const isRolesLocked = !isPlanAtLeast(currentPlan, 'small_clinic');
  const isBrandingLocked = !isPlanAtLeast(currentPlan, 'enterprise');

  // Redirect system admin to admin dashboard
  useEffect(() => {
    if (user?.email?.toLowerCase() === SYSTEM_ADMIN_EMAIL) {
      navigate('/admin', { replace: true });
    }
  }, [user, navigate]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    setMobileOpen(false);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Section title component for sidebar
  const SectionTitle = ({ children }: { children: string }) => (
    <Typography
      variant="overline"
      sx={{
        px: 2,
        pt: 2,
        pb: 0.5,
        display: 'block',
        color: theme.palette.text.secondary,
        fontSize: '0.7rem',
        fontWeight: 600,
        letterSpacing: '0.08em',
      }}
    >
      {children}
    </Typography>
  );

  // Reusable menu item styles
  const menuItemSx = {
    borderRadius: '8px',
    mx: 1,
    my: 0.25,
    py: 1,
    transition: 'all 0.2s ease',
    '&:hover': {
      bgcolor: alpha(theme.palette.primary.main, 0.08),
    },
  };

  const drawer = (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      bgcolor: theme.palette.background.paper,
    }}>
      {/* Header com Logo */}
      <Box sx={{
        px: 2,
        py: 0,
        display: 'flex',
        alignItems: 'center',
        bgcolor: theme.palette.primary.main,
        color: 'white',
        height: '56px',
        minHeight: '56px',
        boxSizing: 'border-box'
      }}>
        <Box
          component="img"
          src={organization?.logoUrl || './logo.png'}
          alt={organization?.name || 'Logo'}
          loading="eager"
          sx={{
            width: 36,
            height: 36,
            mr: 1.5,
            borderRadius: '10px',
            objectFit: 'cover',
            bgcolor: 'white',
            padding: '4px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          }}
        />
        <Typography
          variant="h6"
          noWrap
          component="div"
          sx={{
            fontWeight: 600,
            fontSize: '1.2rem',
            letterSpacing: '-0.02em',
          }}
        >
          {organization?.name || 'Clinafy'}
        </Typography>
      </Box>

      {/* Scrollable content */}
      <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
        {/* Menu Principal */}
        <SectionTitle>Menu Principal</SectionTitle>
        <List sx={{ pt: 0, pb: 1 }}>
          <ListItem disablePadding>
            <ListItemButton onClick={() => handleNavigation('/dashboard')} sx={menuItemSx}>
              <ListItemIcon sx={{ minWidth: 40 }}>
                <DashboardIcon sx={{ color: theme.palette.primary.main, fontSize: 22 }} />
              </ListItemIcon>
              <ListItemText
                primary="Dashboard"
                primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: 500 }}
              />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton id="nav-pacientes" onClick={() => handleNavigation('/patients')} sx={menuItemSx}>
              <ListItemIcon sx={{ minWidth: 40 }}>
                <PeopleIcon sx={{ color: theme.palette.primary.main, fontSize: 22 }} />
              </ListItemIcon>
              <ListItemText
                primary="Pacientes"
                primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: 500 }}
              />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton id="nav-agenda" onClick={() => handleNavigation('/calendar')} sx={menuItemSx}>
              <ListItemIcon sx={{ minWidth: 40 }}>
                <CalendarIcon sx={{ color: theme.palette.primary.main, fontSize: 22 }} />
              </ListItemIcon>
              <ListItemText
                primary="Agenda"
                primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: 500 }}
              />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton id="nav-sessoes" onClick={() => handleNavigation('/sessions')} sx={menuItemSx}>
              <ListItemIcon sx={{ minWidth: 40 }}>
                <SessionsIcon sx={{ color: theme.palette.primary.main, fontSize: 22 }} />
              </ListItemIcon>
              <ListItemText
                primary="Sessões"
                primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: 500 }}
              />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton onClick={() => handleNavigation('/waiting-list')} sx={menuItemSx}>
              <ListItemIcon sx={{ minWidth: 40 }}>
                <WaitingListIcon sx={{ color: theme.palette.primary.main, fontSize: 22 }} />
              </ListItemIcon>
              <ListItemText
                primary="Fila de Espera"
                primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: 500 }}
              />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton onClick={() => handleNavigation('/online-booking')} sx={menuItemSx}>
              <ListItemIcon sx={{ minWidth: 40 }}>
                <OnlineBookingIcon sx={{ color: theme.palette.primary.main, fontSize: 22 }} />
              </ListItemIcon>
              <ListItemText
                primary="Agendamento Online"
                primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: 500 }}
              />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton onClick={() => handleNavigation('/reports')} sx={menuItemSx}>
              <ListItemIcon sx={{ minWidth: 40 }}>
                <ReportsIcon sx={{ color: theme.palette.primary.main, fontSize: 22 }} />
              </ListItemIcon>
              <ListItemText
                primary="Relatórios"
                primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: 500 }}
              />
            </ListItemButton>
          </ListItem>
        </List>

        <Divider sx={{ mx: 2, my: 0.5 }} />

        {/* Gerenciamento */}
        <SectionTitle>Gerenciamento</SectionTitle>
        <List sx={{ pt: 0, pb: 1 }}>
          <FeatureLockedItem
            icon={<GroupsIcon sx={{ color: isTeamLocked ? theme.palette.text.disabled : theme.palette.primary.main, fontSize: 22 }} />}
            label="Equipe"
            path="/team"
            isLocked={isTeamLocked}
            requiredPlan="small_clinic"
            featureKey="team"
            onClick={() => { if (!isTeamLocked) { handleNavigation('/team'); } }}
          />
          <FeatureLockedItem
            icon={<SecurityIcon sx={{ color: isRolesLocked ? theme.palette.text.disabled : theme.palette.primary.main, fontSize: 22 }} />}
            label="Permissões"
            path="/roles"
            isLocked={isRolesLocked}
            requiredPlan="small_clinic"
            featureKey="roles"
            onClick={() => { if (!isRolesLocked) { handleNavigation('/roles'); } }}
          />
          <ListItem disablePadding>
            <ListItemButton onClick={() => handleNavigation('/insurance')} sx={menuItemSx}>
              <ListItemIcon sx={{ minWidth: 40 }}>
                <InsuranceIcon sx={{ color: theme.palette.primary.main, fontSize: 22 }} />
              </ListItemIcon>
              <ListItemText
                primary="Convênios"
                primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: 500 }}
              />
            </ListItemButton>
          </ListItem>
        </List>

        <Divider sx={{ mx: 2, my: 0.5 }} />

        {/* Configurações */}
        <SectionTitle>Configurações</SectionTitle>
        <List sx={{ pt: 0, pb: 1 }}>
          <ListItem disablePadding>
            <ListItemButton id="nav-configuracoes" onClick={() => handleNavigation('/settings')} sx={menuItemSx}>
              <ListItemIcon sx={{ minWidth: 40 }}>
                <SettingsIcon sx={{ color: theme.palette.primary.main, fontSize: 22 }} />
              </ListItemIcon>
              <ListItemText
                primary="Configurações"
                primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: 500 }}
              />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton onClick={() => handleNavigation('/notifications')} sx={menuItemSx}>
              <ListItemIcon sx={{ minWidth: 40 }}>
                <NotificationsIcon sx={{ color: theme.palette.primary.main, fontSize: 22 }} />
              </ListItemIcon>
              <ListItemText
                primary="Notificações"
                primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: 500 }}
              />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton id="nav-documentos" onClick={() => handleNavigation('/documents')} sx={menuItemSx}>
              <ListItemIcon sx={{ minWidth: 40 }}>
                <DocumentsIcon sx={{ color: theme.palette.primary.main, fontSize: 22 }} />
              </ListItemIcon>
              <ListItemText
                primary="Gerar Documentos"
                primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: 500 }}
              />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton onClick={() => handleNavigation('/templates')} sx={menuItemSx}>
              <ListItemIcon sx={{ minWidth: 40 }}>
                <TemplatesIcon sx={{ color: theme.palette.primary.main, fontSize: 22 }} />
              </ListItemIcon>
              <ListItemText
                primary="Biblioteca de Templates"
                primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: 500 }}
              />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton onClick={() => handleNavigation('/export')} sx={menuItemSx}>
              <ListItemIcon sx={{ minWidth: 40 }}>
                <DownloadIcon sx={{ color: theme.palette.primary.main, fontSize: 22 }} />
              </ListItemIcon>
              <ListItemText
                primary="Exportar Dados"
                primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: 500 }}
              />
            </ListItemButton>
          </ListItem>
          <FeatureLockedItem
            icon={<BrandingIcon sx={{ color: isBrandingLocked ? theme.palette.text.disabled : theme.palette.primary.main, fontSize: 22 }} />}
            label="Personalização"
            path="/branding"
            isLocked={isBrandingLocked}
            requiredPlan="enterprise"
            featureKey="customBranding"
            onClick={() => { if (!isBrandingLocked) { handleNavigation('/branding'); } }}
          />
        </List>

        <Divider sx={{ mx: 2, my: 0.5 }} />

        {/* Conta */}
        <SectionTitle>Conta</SectionTitle>
        <List sx={{ pt: 0, pb: 1 }}>
          <ListItem disablePadding>
            <ListItemButton onClick={() => handleNavigation('/subscription')} sx={menuItemSx}>
              <ListItemIcon sx={{ minWidth: 40 }}>
                <CreditCardIcon sx={{ color: theme.palette.primary.main, fontSize: 22 }} />
              </ListItemIcon>
              <ListItemText
                primary="Assinatura"
                primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: 500 }}
              />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton onClick={() => handleNavigation('/referrals')} sx={menuItemSx}>
              <ListItemIcon sx={{ minWidth: 40 }}>
                <ReferralIcon sx={{ color: theme.palette.warning.main, fontSize: 22 }} />
              </ListItemIcon>
              <ListItemText
                primary="Indicações"
                primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: 500 }}
              />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton onClick={() => handleNavigation('/profile')} sx={menuItemSx}>
              <ListItemIcon sx={{ minWidth: 40 }}>
                <PersonIcon sx={{ color: theme.palette.primary.main, fontSize: 22 }} />
              </ListItemIcon>
              <ListItemText
                primary="Meu Perfil"
                primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: 500 }}
              />
            </ListItemButton>
          </ListItem>
        </List>
      </Box>

      {/* Footer fixo com botão Sair */}
      <Box sx={{
        borderTop: `1px solid ${theme.palette.divider}`,
        p: 1,
      }}>
        <ListItemButton
          onClick={handleLogout}
          sx={{
            borderRadius: '8px',
            py: 1,
            '&:hover': {
              bgcolor: alpha(theme.palette.error.main, 0.08),
            }
          }}
        >
          <ListItemIcon sx={{ minWidth: 40 }}>
            <LogoutIcon sx={{ color: theme.palette.error.main, fontSize: 22 }} />
          </ListItemIcon>
          <ListItemText
            primary="Sair"
            primaryTypographyProps={{
              fontSize: '0.9rem',
              fontWeight: 500,
              color: theme.palette.error.main,
            }}
          />
        </ListItemButton>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          borderBottom: `1px solid ${alpha(theme.palette.common.white, 0.1)}`,
          bgcolor: theme.palette.primary.main,
          height: '56px',
          zIndex: (theme) => theme.zIndex.drawer + 1,
          boxSizing: 'border-box',
        }}
      >
        <Toolbar
          sx={{
            height: '56px',
            minHeight: '56px !important',
            px: { xs: 1.5, sm: 2 },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          {/* Lado esquerdo - Menu mobile + Logo (apenas no mobile) */}
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 1, display: { sm: 'none' } }}
            >
              <MenuIcon />
            </IconButton>
            {/* Logo e nome apenas no mobile (no desktop já aparece no sidebar) */}
            <Box sx={{ display: { xs: 'flex', sm: 'none' }, alignItems: 'center' }}>
              <Box
                component="img"
                src={organization?.logoUrl || './logo.png'}
                alt={organization?.name || 'Logo'}
                sx={{
                  width: 28,
                  height: 28,
                  mr: 1,
                  borderRadius: '6px',
                  objectFit: 'cover',
                  bgcolor: 'white',
                  padding: '2px',
                }}
              />
              <Typography
                variant="h6"
                noWrap
                component="div"
                sx={{
                  fontWeight: 600,
                  fontSize: '1rem',
                  letterSpacing: '-0.01em',
                }}
              >
                {organization?.name || 'Clinafy'}
              </Typography>
            </Box>
          </Box>

          {/* Lado direito - Botão dark mode */}
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton
              color="inherit"
              onClick={toggleDarkMode}
              aria-label={isDarkMode ? 'Ativar modo claro' : 'Ativar modo escuro'}
              sx={{
                width: 40,
                height: 40,
                borderRadius: '10px',
                bgcolor: alpha(theme.palette.common.white, 0.1),
                '&:hover': {
                  bgcolor: alpha(theme.palette.common.white, 0.2),
                },
                transition: 'all 0.2s ease',
              }}
            >
              {isDarkMode ? (
                <LightModeIcon sx={{ fontSize: 20 }} />
              ) : (
                <DarkModeIcon sx={{ fontSize: 20 }} />
              )}
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: drawerWidth,
              boxShadow: '0px 3px 10px rgba(0, 0, 0, 0.1)'
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: drawerWidth,
              boxShadow: 'none',
              borderRight: `1px solid ${theme.palette.divider}`
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 1, sm: 2 },
          // No mobile o conteúdo ocupa a largura toda; só desconta a sidebar no desktop
          width: { xs: '100%', sm: `calc(100% - ${drawerWidth}px)` },
          maxWidth: '100%',
          minWidth: 0,
          overflowX: 'hidden',
          backgroundColor: theme.palette.background.default,
          minHeight: '100vh',
          marginTop: '56px'
        }}
      >
        <AppBreadcrumbs />
        <Outlet />
      </Box>

      {/* Help FAB and Drawer */}
      <HelpFAB onClick={() => setHelpOpen(true)} />
      <HelpDrawerContent open={helpOpen} onClose={() => setHelpOpen(false)} />
    </Box>
  );
};

export default Layout;